// ...existing code...
/**
 * contingencyAgent.js
 * Custom React hooks for the Phase 3 Contingency Management Hub.
 * Provides data fetching, mutations, and derived state for transactions and contingencies.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import APIAgent, { API_BASE_URL } from './apiAgent';

// ---------------------------------------------------------------------------
// useTransactionByOffer — load or create a transaction from an offer
// ---------------------------------------------------------------------------
export function useTransactionByOffer(offerId) {
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!offerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/transactions/offer/${offerId}`);
      setTransaction(data);
    } catch (err) {
      if (err.message && err.message.includes('404')) {
        setTransaction(null);
      } else {
        setError(err.message || 'Failed to load transaction');
      }
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => { load(); }, [load]);

  return { transaction, loading, error, reload: load, setTransaction };
}

// ---------------------------------------------------------------------------
// useBuyerTransactions — list all transactions for a buyer
// ---------------------------------------------------------------------------
export function useBuyerTransactions(buyerId) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!buyerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/transactions/buyer/${buyerId}`);
      setTransactions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  useEffect(() => { load(); }, [load]);

  return { transactions, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// useTransaction — single transaction with all contingencies
//
// Switching `transactionId` drops the previous transaction at once (no render
// shows deal A's data under deal B's id), and only the newest request may
// write: a slow response for a deal the user has left is ignored.
// ---------------------------------------------------------------------------
export function useTransaction(transactionId) {
  // Data and error are stored with the id they belong to.
  const [entry, setEntry] = useState({ id: null, transaction: null, error: null });
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    if (!transactionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setEntry((prev) => (prev.id === transactionId && prev.error ? { ...prev, error: null } : prev));
    try {
      const data = await APIAgent.request(`/api/transactions/${transactionId}`);
      if (seq !== requestSeq.current) return;
      setEntry({ id: transactionId, transaction: data, error: null });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setEntry((prev) => ({
        id: transactionId,
        // A failed reload keeps the deal on screen; a failed first load has none.
        transaction: prev.id === transactionId ? prev.transaction : null,
        error: err.message || 'Failed to load transaction',
      }));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { load(); }, [load]);

  const current = entry.id === transactionId;
  return {
    transaction: current ? entry.transaction : null,
    loading,
    error: current ? entry.error : null,
    reload: load,
  };
}

// ---------------------------------------------------------------------------
// useContingency — single contingency detail
// ---------------------------------------------------------------------------
export function useContingency(contingencyId) {
  const [contingency, setContingency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!contingencyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/contingencies/${contingencyId}`);
      setContingency(data);
    } catch (err) {
      setError(err.message || 'Failed to load contingency');
    } finally {
      setLoading(false);
    }
  }, [contingencyId]);

  useEffect(() => { load(); }, [load]);

  // Derived: urgency level for UI
  const urgency = useMemo(() => {
    if (!contingency) return 'none';
    const d = contingency.days_remaining;
    if (d === null || d === undefined) return 'none';
    if (d < 0 || contingency.status === 'expired') return 'critical';
    if (d <= 1) return 'critical';
    if (d <= 3) return 'high';
    if (d <= 7) return 'medium';
    return 'low';
  }, [contingency]);

  return { contingency, loading, error, urgency, reload: load, setContingency };
}

// ---------------------------------------------------------------------------
// useInspectionIssues — issues list + request/respond mutations
// ---------------------------------------------------------------------------
export function useInspectionIssues(contingencyId) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!contingencyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/contingencies/${contingencyId}/inspection/issues`);
      setIssues(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [contingencyId]);

  useEffect(() => { load(); }, [load]);

  const submitRequest = useCallback(async (issueDecisions, notes = '') => {
    setSaving(true);
    setError(null);
    try {
      await APIAgent.request(
        `/api/contingencies/${contingencyId}/inspection/request`,
        'POST',
        { issue_decisions: issueDecisions, notes }
      );
      await load();
      return { success: true };
    } catch (err) {
      setError(err.message || 'Failed to submit repair request');
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, [contingencyId, load]);

  const uploadReport = useCallback(async (file) => {
    setSaving(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(
        `${API_BASE_URL}/api/contingencies/${contingencyId}/inspection/upload`,
        { method: 'POST', credentials: 'include', body: formData }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || `Upload failed (${response.status})`);
      }
      const data = await response.json();
      return { success: true, data };
    } catch (err) {
      setError(err.message || 'Upload failed');
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, [contingencyId]);

  // Totals
  const totals = useMemo(() => {
    const requested = issues.reduce((sum, i) => sum + (parseFloat(i.buyer_requested_amount) || 0), 0);
    const agreed = issues.reduce((sum, i) => sum + (
      i.seller_response === 'accept' ? (parseFloat(i.seller_response_amount) || parseFloat(i.buyer_requested_amount) || 0) : 0
    ), 0);
    const costLow = issues.reduce((sum, i) => sum + (parseFloat(i.estimated_cost_low) || 0), 0);
    const costHigh = issues.reduce((sum, i) => sum + (parseFloat(i.estimated_cost_high) || 0), 0);
    const bySeverity = { critical: 0, major: 0, minor: 0, cosmetic: 0 };
    issues.forEach(i => { if (bySeverity[i.severity] !== undefined) bySeverity[i.severity]++; });
    return { requested, agreed, costLow, costHigh, bySeverity };
  }, [issues]);

  const addIssue = useCallback(async (transactionId, issueData) => {
    setSaving(true);
    setError(null);
    try {
      await APIAgent.request('/api/contingencies/inspection/issues', 'POST', {
        transaction_id: transactionId,
        ...issueData,
      });
      await load();
      return { success: true };
    } catch (err) {
      setError(err.message || 'Failed to add issue');
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, [load]);

  return { issues, loading, error, saving, totals, load, submitRequest, uploadReport, addIssue };
}

// ---------------------------------------------------------------------------
// useNegotiationHistory — inspection negotiation round history
// ---------------------------------------------------------------------------
export function useNegotiationHistory(transactionId) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/contingencies/inspection/history?transaction_id=${transactionId}`);
      setHistory(data);
    } catch (err) {
      setError(err.message || 'Failed to load negotiation history');
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { load(); }, [load]);

  return { history, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// useFinancingMilestones — financing milestone tracker
// ---------------------------------------------------------------------------
export function useFinancingMilestones(contingencyId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!contingencyId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await APIAgent.request(`/api/contingencies/${contingencyId}/financing/milestones`);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load milestones');
    } finally {
      setLoading(false);
    }
  }, [contingencyId]);

  useEffect(() => { load(); }, [load]);

  const updateMilestone = useCallback(async (milestoneId, status, notes = '') => {
    setSaving(true);
    setError(null);
    try {
      await APIAgent.request(
        `/api/contingencies/${contingencyId}/financing/milestones/${milestoneId}`,
        'PUT',
        { status, notes }
      );
      await load();
      return { success: true };
    } catch (err) {
      setError(err.message || 'Failed to update milestone');
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, [contingencyId, load]);

  const requestExtension = useCallback(async (proposedDeadline, reason = '') => {
    setSaving(true);
    setError(null);
    try {
      await APIAgent.request(
        `/api/contingencies/${contingencyId}/financing/extension`,
        'POST',
        { proposed_deadline: proposedDeadline, reason }
      );
      await load();
      return { success: true };
    } catch (err) {
      setError(err.message || 'Failed to request extension');
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, [contingencyId, load]);

  return { milestoneData: data, loading, error, saving, load, updateMilestone, requestExtension };
}

// ---------------------------------------------------------------------------
// useContingencyAdvisor — AI advisor queries
// ---------------------------------------------------------------------------
export function useContingencyAdvisor(contingencyId) {
  const [messages, setMessages] = useState([]);
  const [riskScore, setRiskScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ask = useCallback(async (question, role = 'buyer') => {
    if (!contingencyId || !question.trim()) return;
    const userMsg = { role: 'user', content: question, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(
        `/api/contingencies/${contingencyId}/advisor/query`,
        'POST',
        { question, role }
      );
      const aiMsg = {
        role: 'assistant',
        content: data.answer,
        disclaimer: data.disclaimer,
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      setError(err.message || 'AI advisor unavailable');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I am temporarily unavailable. Please try again shortly.', timestamp: Date.now() }]);
    } finally {
      setLoading(false);
    }
  }, [contingencyId]);

  const loadRiskScore = useCallback(async () => {
    if (!contingencyId) return;
    try {
      const data = await APIAgent.request(`/api/contingencies/${contingencyId}/advisor/risk-score`);
      setRiskScore(data.risk_score);
    } catch (err) {
      console.error('Risk score error:', err);
    }
  }, [contingencyId]);

  return { messages, riskScore, loading, error, ask, loadRiskScore };
}

// ---------------------------------------------------------------------------
// useSellerContingencies — flattened actionable contingency cards for a seller
//
// The seller hub renders one card per contingency that needs the seller's
// response, NOT one card per transaction. We pull the seller's transactions
// (already enriched with property + offer_price by the backend) and explode
// them into per-contingency "card" objects keyed by the contingency id, since
// the respond endpoints are scoped by contingency_id.
// ---------------------------------------------------------------------------
export function useSellerContingencies(sellerId) {
  const [transactions, setTransactions] = useState([]);
  // Wisconsin deals (a `contract_tracking` block): rendered whole by the
  // contract hub, one card per contingency in the signed offer.
  const [contractTransactions, setContractTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sellerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/transactions/seller/${sellerId}`);
      const txs = Array.isArray(data) ? data : [];

      const cards = [];
      txs.forEach((tx) => {
        const propertyFields = {
          transaction_id: tx.id,
          offer_id: tx.offer_id,
          property_address: tx.property_address,
          property_city: tx.property_city,
          property_state: tx.property_state,
          property_zip_code: tx.property_zip_code,
          offer_price: tx.offer_price,
          // WI: inspection/appraisal responses are proposals, not notices.
          is_contract_tracked: Boolean(tx.contract_tracking),
        };
        (tx.contingencies || []).forEach((c) => {
          if (c.contingency_type === 'inspection' && c.status === 'repair_requested') {
            const issues = (c.inspection_issues || []).filter(
              (i) => i.buyer_action && !i.seller_response,
            );
            if (issues.length === 0) return;
            cards.push({
              ...propertyFields,
              id: c.id,
              contingency_type: 'inspection',
              status: c.status,
              inspection_issues: issues,
            });
          } else if (
            c.contingency_type === 'appraisal' &&
            c.status === 'gap_resolution_requested'
          ) {
            cards.push({
              ...propertyFields,
              id: c.id,
              contingency_type: 'appraisal',
              status: c.status,
              appraisal_data: {
                appraised_value: c.appraised_value,
                offer_price: tx.offer_price,
              },
            });
          }
        });
      });

      setTransactions(cards);
      setContractTransactions(txs.filter((tx) => tx.contract_tracking));
    } catch (err) {
      setError(err.message || 'Failed to load seller transactions');
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  return { transactions, contractTransactions, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// useInspectionResponse — submit seller responses to inspection issues
//
// The id passed in is a contingency_id (cards from useSellerContingencies are
// keyed by contingency). The backend expects per-issue decisions with
// `seller_response` + `response_amount`.
// ---------------------------------------------------------------------------
export function useInspectionResponse(contingencyId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submitInspectionResponse = useCallback(async (responses) => {
    setLoading(true);
    setError(null);
    try {
      const issue_decisions = (responses || []).map((r) => {
        const entry = {
          issue_id: r.issue_id,
          seller_response: r.seller_response,
        };
        if (r.credit_amount != null) entry.response_amount = r.credit_amount;
        if (r.response_amount != null) entry.response_amount = r.response_amount;
        if (r.notes) entry.notes = r.notes;
        return entry;
      });
      await APIAgent.request(
        `/api/contingencies/${contingencyId}/inspection/respond`,
        'POST',
        { issue_decisions },
      );
      return { success: true };
    } catch (err) {
      const msg = err.message || 'Failed to submit inspection response';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, [contingencyId]);

  return { submitInspectionResponse, loading, error };
}

// ---------------------------------------------------------------------------
// useAppraisalResponse — submit seller decision on appraisal gap
// ---------------------------------------------------------------------------
export function useAppraisalResponse(contingencyId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // The id passed in is a contingency_id. Backend appraisal/respond accepts
  // { accept, counter_price, notes }. We translate the seller hub's UI choices
  // (reduce_price | split_difference | reject_gap) into that shape using the
  // appraisal context (offer_price, gap, appraised_value) when available.
  const submitAppraisalResponse = useCallback(async (sellerDecision, priceReduction, context = {}) => {
    setLoading(true);
    setError(null);
    try {
      const body = { accept: false };
      if (sellerDecision === 'reduce_price') {
        body.accept = true;
        if (priceReduction != null) body.counter_price = priceReduction;
        body.notes = priceReduction != null
          ? `Seller agrees to reduce price to $${Number(priceReduction).toLocaleString()}.`
          : 'Seller agrees to reduce price to the appraised value.';
      } else if (sellerDecision === 'split_difference') {
        body.accept = false;
        if (context.offerPrice != null && context.gap != null) {
          body.counter_price = context.offerPrice - context.gap / 2;
        }
        body.notes = 'Seller proposes splitting the appraisal gap.';
      } else if (sellerDecision === 'reject_gap') {
        body.accept = false;
        if (context.offerPrice != null) body.counter_price = context.offerPrice;
        body.notes = 'Seller declines any concession for the appraisal gap.';
      } else if (sellerDecision) {
        body.notes = `Seller decision: ${sellerDecision}.`;
      }
      await APIAgent.request(
        `/api/contingencies/${contingencyId}/appraisal/respond`,
        'POST',
        body,
      );
      return { success: true };
    } catch (err) {
      const msg = err.message || 'Failed to submit appraisal response';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, [contingencyId]);

  return { submitAppraisalResponse, loading, error };
}

// ---------------------------------------------------------------------------
// useCreateTransaction — create a transaction from an accepted offer
// ---------------------------------------------------------------------------
export function useCreateTransaction() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createTransaction = useCallback(async (offerId, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request('/api/transactions', 'POST', {
        offer_id: offerId,
        ...options,
      });
      return { success: true, transaction: data };
    } catch (err) {
      const msg = err.message || 'Failed to create transaction';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { createTransaction, loading, error };
}

// ---------------------------------------------------------------------------
// updateContingencyStatus — trigger a state machine transition
// ---------------------------------------------------------------------------
export async function updateContingencyStatus(contingencyId, status, resolutionSummary = null) {
  return APIAgent.request(`/api/contingencies/${contingencyId}/status`, 'PUT', {
    status,
    ...(resolutionSummary ? { resolution_summary: resolutionSummary } : {}),
  });
}
